import { EButtonStyleType, EButtonWidthType, EHorizontalPosition, ESize } from '@common/enums';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

import type { CssVars } from '../../../css-vars';
import { shadowValueVar } from '../../../features/shadow/shadow.css-vars';
import { pxVar } from '../../../mixins/number-with-unit/number-with-unit.css-vars';
import type { ThemeColorCssResolver } from '../../common/styles/css-vars';
import { getThemedButtonCssVars, getThemedButtonHoverClasses } from '../../form/styles/themed-button.css-vars';
import type { ButtonEntity, ButtonItem, LegacyButtonZero } from '../schemas';

/**
 * CSS custom properties for the button widget — the LESS twin of the former
 * JSS `getStyles`. ONE module shared by both apps; the static rules consuming
 * the variables live in each component's `.less`
 * (`button-view.component.less` / `button.component.less`).
 *
 * Group-level variables are computed per component (editor: `computed`
 * signals, published renderer: getters — the video-widget pattern) from the
 * lookup tables exported here; only the per-item maps are involved enough to
 * warrant shared functions.
 *
 * A `null` value removes the property from the element, so the consuming
 * declaration falls back to its `var()` default — mirroring the omitted-key
 * behaviour of the old JSS object.
 */
export type ButtonCssVars = CssVars;

export const CONTAINER_INNER_MARGIN = {
    [ESize.S]: '-5px',
    [ESize.M]: '-10px',
    [ESize.L]: '-15px',
};

export const BUTTON_OUTER_MARGIN = {
    [ESize.S]: '5px',
    [ESize.M]: '10px',
    [ESize.L]: '15px',
};

/**
 * The slice of a button widget these builders read. Structural on purpose: the
 * editor's `WebLegacyWidgetEntity<ButtonEntity['data']>` and the builder's own widget
 * shape both satisfy it without a cast.
 */
export type ButtonWidgetLike = {
    type?: string;
    data: ButtonEntity['data'];
};

/**
 * Variables for one rendered button (the `.sp-button-item` anchor/span).
 *
 * `resolveColor` is the host's theme colour helper — theme resolution lives in
 * `@sites/site-theme`, an Angular library, and this package stays framework-free.
 */
export function getButtonItemCssVars(
    widget: ButtonWidgetLike,
    button: ButtonItem,
    resolveColor: ThemeColorCssResolver
): ButtonCssVars {
    const isZero = widget.type === 'zero';
    const widthType = button.widthType || EButtonWidthType.auto;

    const vars: ButtonCssVars = {
        '--btn-flex-direction': button.buttonIcon?.position === EHorizontalPosition.L ? 'row' : 'row-reverse',
    };

    if (isZero) {
        vars['--btn-min-width'] = pxVar((widget.data as LegacyButtonZero['data']).position.width);
    } else {
        vars['--btn-width'] = widthType !== EButtonWidthType.auto ? '100%' : 'auto';
        vars['--btn-max-width'] =
            widthType === EButtonWidthType.fixed && button.widthValue != null ? pxVar(button.widthValue) : 'auto';
    }

    if (button.isShadow) {
        const s = button.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        vars['--btn-shadow'] = shadowValueVar(s);
    }

    if (button.styleType === EButtonStyleType.custom) {
        // Theme colours are stored as a stop list; the button paints the first stop.
        const colorCss = (value: string | string[] | undefined): string | null => {
            const color = Array.isArray(value) ? value[0] : value;
            return color ? resolveColor(color) || null : null;
        };

        Object.assign(vars, getThemedButtonCssVars(button, '--btn', colorCss));
    }

    return vars;
}

/**
 * Modifier classes gating the variable-driven rules — needed because an
 * unset custom property would otherwise reset the declaration to its initial
 * value instead of leaving the themed/global styles intact (e.g. `box-shadow:
 * var(--btn-shadow)` on a non-shadowed themed button would compute to `none`
 * and clobber the theme).
 */
export function getButtonItemCssClasses(button: ButtonItem): string {
    const classes: string[] = [];

    if (button.isShadow) {
        classes.push('btn-shadow');
    }

    if (button.styleType === EButtonStyleType.custom) {
        classes.push('btn-custom');

        classes.push(...getThemedButtonHoverClasses(button, 'btn'));
    }

    return classes.join(' ');
}
